/**
 * packages/shared/src/fmv/manufacturer-logo.ts
 *
 * Manufacturer brand-logo exceptions — hand-maintained lists that decide
 * how (or whether) a manufacturer's mark renders. Every renderer of a
 * manufacturer's logo (React `ManufacturerLogo` in apps/customer and
 * apps/operations, and the vanilla listbox in apps/fmv, a separate Vite app
 * with no React) reads this ONE copy so the three surfaces can never drift
 * on which brands have no mark or need a light backing — the same no-drift
 * rationale as fmv/browse.ts (see that module's header).
 *
 * The SVGs themselves are NOT shared — each app bundles its own copy under
 * public/mfr-logos/<id>.svg (apps/customer, apps/operations, apps/fmv are
 * three separately deployed origins/buckets) — only the two exception lists
 * live here. apps/exchange (SIL-1550) does NOT add a fourth copy: its vite
 * build copies apps/customer/public/mfr-logos into dist/mfr-logos.
 */

/** Manufacturer ids with no brand mark on file — skip the image request
 * rather than let it 404. Add an id here as a markless brand appears in the
 * catalog, or drop an `<id>.svg` into every app's `public/mfr-logos/`
 * instead (customer, operations, fmv). */
export const NO_LOGO_IDS = new Set<string>(['generic', 'cloud_vm', 'ampere', 'pure_storage']);

/** Manufacturer ids whose mark is near-black and vanishes on a dark theme —
 * render it on a literal white chip so it stays legible in both themes.
 * Add any dark-marked brand here. */
export const NEEDS_WHITE_BG_IDS = new Set<string>(['hpe', 'supermicro', 'micron', 'samsung', 'juniper_networks']);

export function hasNoLogo(id: string | null | undefined): boolean {
  return !!id && NO_LOGO_IDS.has(id);
}

export function needsWhiteBg(id: string | null | undefined): boolean {
  return !!id && NEEDS_WHITE_BG_IDS.has(id);
}
